import React, { useEffect, useMemo, useState } from "react";
import { userService } from "../services/userService";
import ReactCountryFlag from "react-country-flag";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import {
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuEllipsisVertical,
  LuFilter,
  LuPencil,
  LuSearch,
  LuX,
} from "react-icons/lu";
import companyController from "../controllers/companyController";
import ExportIcon from "../icons/ExportIcon";
import RemoveUserIcon from "../icons/RemoveUserIcon";
import ActiveIcon from "../icons/ActiveIcon";
import InviteIcon from "../icons/InviteIcon";
import TrachRedIcon from "../icons/TrachRedIcon";
import { getStoredUserId } from "./vendorSession";
import { getLanguage, translateLabel, useTranslationSnapshot } from "./../translations/runtime";

const userFeedbackLabels = new Map([
  ["Company id was not found for this user.", "txt_web_vendor_company_id_was_not_found_for_this_user"],
  ["Unable to load users.", "txt_web_vendor_unable_to_load_users"],
  ["User id was not found for this user.", "txt_web_vendor_user_id_was_not_found_for_this_user"],
  ["You cannot delete the signed-in user.", "txt_web_vendor_you_cannot_delete_the_signed_in_user"],
  ["Unable to delete user.", "txt_web_vendor_unable_to_delete_user"],
]);
const translateUserFeedback = (message) => {
  const id = userFeedbackLabels.get(message);
  return id ? translateLabel(id, message) : message;
};
const userStatusLabels = {
  Active: "txt_common_active", Inactive: "txt_web_vendor_inactive",
  "Invite Sent": "txt_web_vendor_invite_sent", Pending: "txt_common_pending",
};

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent);

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? {};

const COUNTRY_BY_CALLING_CODE = getCountries().reduce(
  (countryByCallingCode, country) => {
    const callingCode = getCountryCallingCode(country);

    if (!countryByCallingCode[callingCode]) {
      countryByCallingCode[callingCode] = country;
    }

    return countryByCallingCode;
  },
  {
    1: "US",
    44: "GB",
  },
);

const toDisplayLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeStatusKey = (value) => {
  const normalized = String(value || "")
    .toLowerCase()
    .replace(/[\s_]+/g, "-");

  if (normalized === "active") return "active";
  if (normalized === "inactive") return "inactive";
  if (normalized.includes("invite") || normalized.includes("pending")) {
    return "invite-sent";
  }

  return normalized || "active";
};

const formatDate = (value, locale) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString(locale, {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (firstName, lastName, email) => {
  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join("");

  return (initials || String(email || "U").charAt(0)).toUpperCase();
};

const getDigitsOnly = (value) => String(value || "").replace(/\D/g, "");

const getPhoneMeta = (employee) => {
  const rawPhoneNumber = firstDefined(employee.phoneNumber, employee.phone, "");
  const phoneNumber = String(rawPhoneNumber || "-");
  const parsedPhone = String(rawPhoneNumber || "").startsWith("+")
    ? parsePhoneNumber(String(rawPhoneNumber))
    : null;
  const suffix = getDigitsOnly(
    firstDefined(
      employee.suffix,
      employee.phoneSuffix,
      employee.countryCallingCode,
      parsedPhone?.countryCallingCode,
      "",
    ),
  );
  const countryCode = parsedPhone?.country || COUNTRY_BY_CALLING_CODE[suffix] || "";

  if (!suffix || phoneNumber.startsWith("+")) {
    return {
      countryCode,
      phone: phoneNumber,
      suffix,
    };
  }

  const phoneDigits = getDigitsOnly(phoneNumber);
  const localPhone =
    phoneDigits && phoneDigits.startsWith(suffix)
      ? phoneDigits.slice(suffix.length)
      : phoneNumber;

  return {
    countryCode,
    phone: `+${suffix} ${localPhone || phoneNumber}`,
    suffix,
  };
};

const isCreatedUser = (employee, createdUser) => {
  if (!createdUser?.email) return false;

  return (
    String(createdUser.email).toLowerCase() ===
    String(employee.email || "").toLowerCase()
  );
};

const normalizeUser = (employee, index, createdUser) => {
  const createdUserMatch = isCreatedUser(employee, createdUser);
  const userID = employee.userId || employee.userID || "";
  const firstName = employee.firstName || "";
  const lastName = employee.lastName || "";
  const email = employee.email || "-";
  const name = [firstName, lastName].filter(Boolean).join(" ") || email;
  const createdAt = createdUserMatch
    ? createdUser.createdAt
    : employee.employmentStartDate;
  const statusSource = createdUserMatch
    ? firstDefined(createdUser.status, createdUser.employmentStatus, "INVITE_SENT")
    : firstDefined(employee.employmentStatus, employee.status, "ACTIVE");
  const phoneMeta = getPhoneMeta(employee);

  return {
    countryCode: phoneMeta.countryCode,
    date: formatDate(createdAt),
    dateValue: createdAt,
    email,
    id: userID || employee.email || `employee-${index}`,
    initials: getInitials(firstName, lastName, email),
    firstName,
    lastName,
    name,
    phone: phoneMeta.phone,
    phoneNumber: employee.phoneNumber || "",
    raw: employee,
    role: employee.roleName || "-",
    roleName: employee.roleName || "",
    status: toDisplayLabel(statusSource),
    statusKey: normalizeStatusKey(statusSource),
    suffix: phoneMeta.suffix,
    userID,
  };
};

const getStatusClassName = (statusKey) => {
  if (statusKey === "active") return "active";
  if (statusKey === "inactive") return "inactive";
  return "invited";
};

function UserManagementUsers() {
  useTranslationSnapshot();
  const location = useLocation();
  const navigate = useNavigate();
  const isPreview = location.pathname.includes("-preview");
  const createdUser = location.state?.createdUser || null;
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [size] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchUsers = async () => {
      setLoading(true);
      setError("");

      try {
        const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;
        const companyID = userService.getUserData().Compid;
        const resolvedCompanyId = companyId || companyID;

        if (!resolvedCompanyId) {
          throw new Error("Company id was not found for this user.");
        }

        const response = await companyController.getCompanyEmployees({
          Authorization,
          companyId: resolvedCompanyId,
          page,
          search: search.trim(),
          size,
        });

        if (response?.isAxiosError || response?.response) {
          throw response;
        }

        const payload = unwrapPayload(response);
        const content = Array.isArray(payload?.content)
          ? payload.content
          : Array.isArray(payload)
            ? payload
            : [];

        if (!isCurrentRequest) return;

        setUsers(
          content.map((employee, index) =>
            normalizeUser(employee, index, createdUser),
          ),
        );
        setTotalElements(
          isPresent(payload?.totalElements)
            ? Number(payload.totalElements)
            : content.length,
        );
        setTotalPages(
          isPresent(payload?.totalPages) ? Number(payload.totalPages) : 1,
        );
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setUsers([]);
        setTotalElements(0);
        setTotalPages(0);
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load users.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchUsers();

    return () => {
      isCurrentRequest = false;
    };
  }, [createdUser, page, search, size]);

  const visibleUsers = useMemo(() => {
    return users.filter((user) => {
      return (
        statusFilter === "all" ||
        user.statusKey === statusFilter
      );
    });
  }, [statusFilter, users]);

  const totalPagesSafe = Math.max(totalPages || 1, 1);

  const paginationPages = useMemo(() => {
    const maxVisiblePages = 5;
    const start = Math.max(
      1,
      Math.min(page - 2, totalPagesSafe - maxVisiblePages + 1),
    );
    const end = Math.min(totalPagesSafe, start + maxVisiblePages - 1);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [page, totalPagesSafe]);

  const openEditUser = (user) => {
    const userId = user.userID || user.id;
    const path = isPreview
      ? `/vendor-edit-user-preview/${userId}`
      : `/vendor-user-management/edit/${userId}`;
    navigate(path, { state: { user } });
  };

  const deleteUser = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    setError("");

    try {
      const Authorization = userService.getJwt();
      const companyID = userService.getUserData().Compid;
      const signedInUserID = getStoredUserId();
      const userID =
        userToDelete.userID ||
        userToDelete.raw?.userId ||
        userToDelete.raw?.userID ||
        "";

      if (!companyID) {
        throw new Error("Company id was not found for this user.");
      }
      if (!userID) {
        throw new Error("User id was not found for this user.");
      }
      if (signedInUserID && signedInUserID === userID) {
        throw new Error("You cannot delete the signed-in user.");
      }

      const response = await companyController.deleteCompanyEmployee({
        Authorization,
        removeEmployeeRequestDTO: {
          companyID,
          userID,
        },
      });

      if (response?.isAxiosError || response?.response) {
        throw response;
      }

      setUsers((currentUsers) =>
        currentUsers.filter((user) => user.userID !== userID),
      );
      setUserToDelete(null);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to delete user.",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <section className="vendor-users-panel">
      <div className="vendor-users-toolbar">
        <label className="vendor-users-search">
          <LuSearch aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder={translateLabel("txt_web_vendor_search_users_placeholder", "Search users...")}
            aria-label={translateLabel("txt_web_vendor_search_users", "Search users")}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </label>

        <label className="vendor-users-filter">
          <LuFilter aria-hidden="true" />
          <select
            value={statusFilter}
            aria-label={translateLabel("txt_web_vendor_filter_users_by_status", "Filter users by status")}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">{translateLabel("txt_web_vendor_filter", "Filter")}</option>
            <option value="active">{translateLabel("txt_common_active", "Active")}</option>
            <option value="invite-sent">{translateLabel("txt_web_vendor_invite_sent", "Invite Sent")}</option>
            <option value="inactive">{translateLabel("txt_web_vendor_inactive", "Inactive")}</option>
          </select>
          <LuChevronDown aria-hidden="true" />
        </label>

        <button className="vendor-users-export" type="button">
          <ExportIcon />
          {translateLabel("txt_web_vendor_export", "Export")}
        </button>
      </div>

      <div className="vendor-users-table-wrap">
        <table className="vendor-users-table">
          <thead>
            <tr>
              <th className="vendor-users-checkbox-cell">
                <input type="checkbox" aria-label={translateLabel("txt_web_vendor_select_all_users", "Select all users")} />
              </th>
              <th>{translateLabel("txt_common_user", "User")}</th>
              <th>{translateLabel("txt_end_user_email", "Email")}</th>
              <th>{translateLabel("input_phone_number", "Phone Number")}</th>
              <th>{translateLabel("txt_common_status", "Status")}</th>
              <th>{translateLabel("txt_web_vendor_role", "Role")}</th>
              <th>{translateLabel("txt_common_actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7}>{translateLabel("txt_web_vendor_loading_users", "Loading users...")}</td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={7} role="alert">{translateUserFeedback(error)}</td>
              </tr>
            )}
            {!loading && !error && visibleUsers.length === 0 && (
              <tr>
                <td colSpan={7}>{translateLabel("txt_web_vendor_no_users_found", "No users found.")}</td>
              </tr>
            )}
            {!loading && !error && visibleUsers.map((user) => (
              <tr key={user.id}>
                <td className="vendor-users-checkbox-cell">
                  <input
                    type="checkbox"
                    aria-label={translateLabel("txt_common_select_value1", "Select {value1}", { value1: user.name })}
                  />
                </td>
                <td>
                  <div className="vendor-user-identity">
                    <span>{user.initials}</span>
                    <strong>{user.name}</strong>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className="vendor-user-phone">
                    {user.countryCode && (
                      <ReactCountryFlag
                        className="vendor-user-phone-flag"
                        countryCode={user.countryCode}
                        svg
                        title={user.countryCode}
                      />
                    )}
                    {user.phone}
                  </span>
                </td>
                <td>
                  <span
                    className={`vendor-user-status ${getStatusClassName(
                      user.statusKey,
                    )}`}
                  >
                    {user.status === "Active" ? (
                      <ActiveIcon />
                    ) : (
                      <InviteIcon />
                    )}
                    <span>
                      <strong>{userStatusLabels[user.status] ? translateLabel(userStatusLabels[user.status], user.status) : user.status}</strong>
                      <small>{formatDate(user.dateValue, getLanguage() === "ar" ? "ar-LB-u-ca-gregory" : "en-US")}</small>
                    </span>
                  </span>
                </td>
                <td>{user.role}</td>
                <td>
                  <div className="vendor-user-actions">
                    <button
                      className="vendor-user-actions-button"
                      type="button"
                      aria-label={translateLabel("txt_web_vendor_actions_for_value1", "Actions for {value1}", { value1: user.name })}
                      aria-expanded={openMenuId === user.id}
                      onClick={() =>
                        setOpenMenuId((current) =>
                          current === user.id ? null : user.id,
                        )
                      }
                    >
                      <LuEllipsisVertical aria-hidden="true" />
                    </button>
                    {openMenuId === user.id && (
                      <div className="vendor-user-action-menu">
                        <button
                          type="button"
                          onClick={() => openEditUser(user)}
                        >
                          <LuPencil aria-hidden="true" />
                          <span>{translateLabel("txt_web_vendor_edit_details", "Edit Details")}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setUserToDelete(user);
                            setOpenMenuId(null);
                          }}
                        >
                          <RemoveUserIcon />
                          <span>{translateLabel("txt_web_vendor_remove_user", "Remove User")}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="vendor-users-pagination">
        <div> {translateLabel("txt_common_rows_per_page", "Rows per page")} <button type="button">{size}</button>
          <span>{translateLabel("txt_web_vendor_user_total_rows", "of {count} rows", { count: totalElements })}</span>
        </div>
        <nav aria-label={translateLabel("txt_web_vendor_user_table_pagination", "User table pagination")}>
          <button
            type="button"
            aria-label={translateLabel("txt_common_previous_page", "Previous page")}
            disabled={page <= 1 || loading}
            onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
          >
            <LuChevronLeft className="navigation-direction-icon" aria-hidden="true" />
          </button>
          {paginationPages[0] > 1 && (
            <>
              <button type="button" onClick={() => setPage(1)}>
                1
              </button>
              {paginationPages[0] > 2 && <span>...</span>}
            </>
          )}
          {paginationPages.map((pageNumber) => (
            <button
              className={pageNumber === page ? "active" : ""}
              type="button"
              key={pageNumber}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}
          {paginationPages[paginationPages.length - 1] < totalPagesSafe && (
            <>
              {paginationPages[paginationPages.length - 1] <
                totalPagesSafe - 1 && <span>...</span>}
              <button type="button" onClick={() => setPage(totalPagesSafe)}>
                {totalPagesSafe}
              </button>
            </>
          )}
          <button
            type="button"
            aria-label={translateLabel("txt_common_next_page", "Next page")}
            disabled={page >= totalPagesSafe || loading}
            onClick={() =>
              setPage((currentPage) =>
                Math.min(totalPagesSafe, currentPage + 1),
              )
            }
          >
            <LuChevronRight className="navigation-direction-icon" aria-hidden="true" />
          </button>
        </nav>
      </div>

      {userToDelete && (
        <div className="vendor-delete-user-backdrop">
          <div
            className="vendor-delete-user-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vendor-delete-user-title"
          >
            <button
              className="vendor-delete-user-close"
              type="button"
              aria-label={translateLabel("txt_web_vendor_close_delete_user", "Close Delete User")}
              onClick={() => setUserToDelete(null)}
            >
              <LuX aria-hidden="true" />
            </button>

            <div className="vendor-delete-user-icon">
              <TrachRedIcon />
            </div>
            <div className="vendor-delete-user-title" id="vendor-delete-user-title">
              {translateLabel("txt_web_vendor_delete_user_title", "Delete User")}
            </div>
            <div className="vendor-delete-user-message">
              {translateLabel("txt_web_vendor_delete_user_confirmation", "Are you sure you want to delete this user?")}
            </div>
            <div className="vendor-delete-user-actions">
              <button type="button" disabled={isDeleting} onClick={deleteUser}>
                {isDeleting ? translateLabel("txt_end_user_deleting", "Deleting...") : translateLabel("lnk_delete", "Delete")}
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setUserToDelete(null)}
              > {translateLabel("btn_cancel", "Cancel")} </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default UserManagementUsers;
